import React, { memo, useState } from 'react';
import { SubmitHandler, useForm } from 'react-hook-form';
import { useTranslation } from 'next-i18next';

import { Icon, IconButton } from 'components';
import Avatar from 'components/Avatar';
import ButtonV3 from 'components/ComponentV2/ButtonV3';
import { InputV2 } from 'components/ComponentV2';
import { Form } from 'components/form';
import Modal from 'components/Modal';
import { Col } from 'components/layout';
import { useCommon } from 'hooks/useCommon';
import {
  CommitteeFormAttributes,
  CommitteeAttributes,
} from 'lib/models/committee';
import { PAGE_ROUTES } from 'lib/page-routes';

import { convertBase64 } from 'utils/format';
import styles from './MemberUpdateModal.module.scss';

interface EditModalProps {
  onClose: () => void;
  isOpen: boolean;
  userData: CommitteeFormAttributes;
  onSave: (data: CommitteeAttributes, avatar: string) => void;
}

export const EditMemberModal: React.FC<EditModalProps> = memo(
  ({ onClose, isOpen, userData, onSave }) => {
    const [t] = useTranslation('committee');
    const { ...defaultValues } = userData;
    const {
      register,
      handleSubmit,
      formState: { errors, isSubmitting },
      setValue,
      watch,
    } = useForm({ defaultValues });

    const avatar = watch('avatar');

    const onSubmit: SubmitHandler<CommitteeFormAttributes> = (data) => {
      data.name = `${data.first_name} ${data.last_name}`;
      const { avatar, ...newUserData } = data;
      const profileImage = avatar?.file ? avatar.file : '';
      onSave(newUserData, profileImage);
      onClose();
    };

    const [showFileSizeError, setShowFileSizeError] = useState<boolean>(false);

    const uploadImage = async (event: React.ChangeEvent<HTMLInputElement>) => {
      const file = event.target.files && event.target.files[0];
      if (file && file?.size / 1000 < 5001) {
        setShowFileSizeError(false);
        const base64 = (await convertBase64(file as Blob)) as string;
        setValue('avatar', { file: base64 });
      } else {
        setShowFileSizeError(true);
      }
    };

    return (
      <Modal
        contentClassName={styles.editModal}
        isOpen={isOpen}
        onRequestClose={onClose}
        isFullScreen={false}
      >
        <IconButton
          size="small"
          iconName="close"
          className={styles.cancelIcon}
          onClick={onClose}
        />
        <div className={styles.title}>{t('edit-member-modal.title')}</div>
        <Form
          autoComplete="off"
          onSubmit={(e) => {
            e.preventDefault();
            e.stopPropagation();
            handleSubmit(onSubmit)(e);
          }}
          id="modal-form"
        >
          <div className={styles.editForm}>
            <div className={styles.imageContainer}>
              <label htmlFor="upload-profile-image">
                <Avatar
                  className={styles.avatar}
                  icon={avatar ? avatar.file : userData.avatar_url}
                  name={userData.name}
                  size="xxlarge"
                />
              </label>
              <input
                type="file"
                accept="image/*"
                id="upload-profile-image"
                onChange={uploadImage}
                hidden
              />
              <Icon
                iconName="icon_pencil"
                className={styles.imageIcon}
                size="small"
              />
            </div>
            {showFileSizeError ? (
              <div className={styles['error-container']}>
                <Icon iconName={'info'} size="xsmallplus"></Icon>
                <span>{t('edit-member-modal.size-error')}</span>
              </div>
            ) : (
              <></>
            )}
            <div className={styles.name}>
              <InputV2
                labelClassName={styles.label}
                inputClassName={styles.placeholder}
                type="text"
                label={t('edit-member-modal.first-name.label')}
                placeholder={t('edit-member-modal.first-name.placeholder')}
                {...register('first_name', {
                  required: true,
                })}
                isInvalid={!!errors.first_name}
              />
              <InputV2
                labelClassName={styles.label}
                inputClassName={styles.placeholder}
                type="text"
                label={t('edit-member-modal.last-name.label')}
                placeholder={t('edit-member-modal.last-name.placeholder')}
                {...register('last_name', {
                  required: true,
                })}
                isInvalid={!!errors.last_name}
              />
            </div>
            <Col span={24}>
              <InputV2
                className={styles.position}
                labelClassName={styles.label}
                inputClassName={styles.placeholder}
                type="text"
                label={t('edit-member-modal.position.label')}
                placeholder={t('edit-member-modal.position.placeholder')}
                {...register('role', {
                  required: `${t('edit-member-modal.position.error-message')}`,
                })}
                isInvalid={!!errors.role}
              />
              {errors.role ? (
                <p className={styles.errorMessage}>{errors.role.message}</p>
              ) : null}
            </Col>
          </div>
          <div className={styles.footer}>
            <ButtonV3
              className={styles.button}
              type="submit"
              disabled={isSubmitting}
            >
              {t('edit-member-modal.actions.done')}
            </ButtonV3>
          </div>
        </Form>
      </Modal>
    );
  }
);

interface RemoveModalProps {
  onClose: () => void;
  isOpen: boolean;
  userName: string;
  onRemove: () => void;
}

export const RemoveMemberModal: React.FC<RemoveModalProps> = memo(
  ({ onClose, isOpen, userName, onRemove }) => {
    const [t] = useTranslation('committee');
    return (
      <Modal
        contentClassName={styles.removeModal}
        isOpen={isOpen}
        onRequestClose={onClose}
        isFullScreen={false}
      >
        <IconButton
          size="small"
          iconName="close"
          onClick={onClose}
          className={styles.closeIcon}
        />
        <p className={styles.title}>{t('remove-member-modal.title')}</p>
        <p className={styles.detail}>
          {t('remove-member-modal.description')}
          <mark>{`${userName}?`}</mark>
        </p>
        <div className={styles.footer}>
          <ButtonV3
            color="secondary"
            onClick={onClose}
            className={styles.cancel}
          >
            {t('remove-member-modal.actions.cancel')}
          </ButtonV3>
          <ButtonV3
            onClick={() => {
              onRemove();
              onClose();
            }}
            className={styles.remove}
          >
            {t('remove-member-modal.actions.remove')}
          </ButtonV3>
        </div>
      </Modal>
    );
  }
);

interface InviteMembersProps {
  showButton: boolean;
}

export const InviteMembersBanner: React.FC<InviteMembersProps> = memo(
  ({ showButton }) => {
    const [t] = useTranslation('committee');
    const { onShare } = useCommon();
    return (
      <div className={styles.empty}>
        <Icon className={styles.iconSize} iconName="info"></Icon>
        <div className={styles.content}>
          <p className={styles.title}>{t('invite-members-banner.title')}</p>
          <p className={styles.description}>
            {t('invite-members-banner.description')}
          </p>
        </div>
        {showButton ? (
          <ButtonV3
            className={styles.button}
            color="secondary"
            onClick={() =>
              onShare(
                'Manager Signup',
                `${window.location.origin}${PAGE_ROUTES.MANAGER_SIGN_UP}`,
                {
                  title: `${t('invite-link-copied-notification.title')}`,
                  message: `${t('invite-link-copied-notification.message')}`,
                }
              )
            }
          >
            {t('invite-members-banner.actions.invite')}
          </ButtonV3>
        ) : null}
      </div>
    );
  }
);

EditMemberModal.displayName = 'EditMemberModal';
RemoveMemberModal.displayName = 'RemoveMemberModal';
InviteMembersBanner.displayName = 'RenderEmptyContent';
